import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getGetMessagingCapabilitiesQueryKey } from '../../api/generated/messaging/messaging'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { useT } from '../../i18n'
import {
  MESSAGING_MODES,
  messagingRolloutI18n,
  type MessagingMode,
} from '../../i18n/settings/messaging-rollout'
import { apiUrl } from '../../lib/api'

const CONFIG_QUERY_KEY = ['admin', 'config', 'messaging_mode'] as const

class MessagingConfigError extends Error {
  readonly status?: number

  constructor(status?: number) {
    super('Messaging mode config is unavailable')
    this.status = status
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseMessagingMode(value: unknown): MessagingMode {
  if (
    !isRecord(value) ||
    value.config_key !== 'messaging_mode' ||
    value.value_type !== 'string' ||
    !isRecord(value.config_value)
  ) {
    throw new MessagingConfigError()
  }

  const mode = value.config_value.value
  if (typeof mode !== 'string' || !MESSAGING_MODES.includes(mode as MessagingMode)) {
    throw new MessagingConfigError()
  }

  return mode as MessagingMode
}

async function responseMode(response: Response): Promise<MessagingMode> {
  if (!response.ok) throw new MessagingConfigError(response.status)

  try {
    return parseMessagingMode(await response.json())
  } catch (error) {
    if (error instanceof MessagingConfigError) throw error
    throw new MessagingConfigError(response.status)
  }
}

async function readMessagingMode(): Promise<MessagingMode> {
  return responseMode(await fetch(apiUrl('/admin/config/messaging_mode')))
}

async function writeMessagingMode(mode: MessagingMode): Promise<MessagingMode> {
  return responseMode(
    await fetch(apiUrl('/admin/config/messaging_mode'), {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        config_value: { value: mode },
        value_type: 'string',
      }),
    }),
  )
}

type MessagingModeCardProps = {
  mode: MessagingMode
  checked: boolean
  current: boolean
  pending: boolean
  label: string
  description: string
  currentLabel: string
  pendingLabel: string
  onChange: (mode: MessagingMode) => void
}

function MessagingModeCard({
  mode,
  checked,
  current,
  pending,
  label,
  description,
  currentLabel,
  pendingLabel,
  onChange,
}: MessagingModeCardProps) {
  const descriptionId = `messaging-mode-${mode}-description`

  return (
    <label
      className={`flex min-h-20 cursor-pointer items-start gap-3 rounded-[var(--radius-panel)] border p-4 transition-colors focus-within:shadow-[var(--shadow-focus)] ${
        checked
          ? 'border-[var(--color-action)] bg-[var(--color-surface)]'
          : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-muted)]'
      }`}
    >
      <input
        type="radio"
        name="messaging-mode"
        value={mode}
        checked={checked}
        aria-label={label}
        aria-describedby={descriptionId}
        onChange={() => onChange(mode)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-action)]"
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[var(--color-text)]">{label}</span>
          {current ? <Badge variant="success">{currentLabel}</Badge> : null}
          {pending ? <Badge variant="warning">{pendingLabel}</Badge> : null}
        </span>
        <span
          id={descriptionId}
          className="mt-1 block text-sm leading-5 text-[var(--color-text-muted)]"
        >
          {description}
        </span>
      </span>
    </label>
  )
}

export function MessagingRolloutControl() {
  const t = useT(messagingRolloutI18n)
  const queryClient = useQueryClient()
  const [draftMode, setDraftMode] = useState<MessagingMode | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [saved, setSaved] = useState(false)
  const modeQuery = useQuery<MessagingMode, MessagingConfigError>({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: readMessagingMode,
    retry: false,
  })
  const currentMode = modeQuery.data ?? null
  const selectedMode = draftMode ?? currentMode
  const hasPendingChange =
    currentMode !== null && draftMode !== null && draftMode !== currentMode
  const mutation = useMutation<MessagingMode, MessagingConfigError, MessagingMode>({
    mutationFn: writeMessagingMode,
    onSuccess: (authoritativeMode) => {
      queryClient.setQueryData(CONFIG_QUERY_KEY, authoritativeMode)
      setDraftMode(null)
      setReviewOpen(false)
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: CONFIG_QUERY_KEY })
      void queryClient.invalidateQueries({
        queryKey: getGetMessagingCapabilitiesQueryKey(),
      })
    },
  })

  const chooseMode = (mode: MessagingMode) => {
    if (!currentMode) return
    setSaved(false)
    setDraftMode(mode === currentMode ? null : mode)
  }

  return (
    <section className="max-w-3xl space-y-5" aria-labelledby="messaging-rollout-title">
      <header>
        <h2
          id="messaging-rollout-title"
          className="text-lg font-semibold text-[var(--color-text)]"
        >
          {t.title}
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-5 text-[var(--color-text-muted)]">
          {t.description}
        </p>
      </header>

      {modeQuery.isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite">
          <span className="sr-only">{t.loading}</span>
          {MESSAGING_MODES.map((mode) => (
            <div
              key={mode}
              aria-hidden="true"
              className="h-24 animate-pulse rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : modeQuery.isError ? (
        <div
          role="alert"
          className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4"
        >
          <p className="text-sm font-medium text-[var(--color-text)]">
            {modeQuery.error.status === 403 ? t.permissionDenied : t.loadError}
          </p>
          {modeQuery.error.status !== 403 ? (
            <Button
              type="button"
              variant="outline"
              className="mt-3"
              onClick={() => void modeQuery.refetch()}
            >
              {t.retry}
            </Button>
          ) : null}
        </div>
      ) : currentMode ? (
        <>
          <fieldset className="space-y-3" role="radiogroup">
            <legend className="sr-only">{t.title}</legend>
            {MESSAGING_MODES.map((mode) => (
              <MessagingModeCard
                key={mode}
                mode={mode}
                checked={selectedMode === mode}
                current={currentMode === mode}
                pending={hasPendingChange && draftMode === mode}
                label={t.modes[mode].label}
                description={t.modes[mode].description}
                currentLabel={t.current}
                pendingLabel={t.pending}
                onChange={chooseMode}
              />
            ))}
          </fieldset>

          <p
            className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-3 text-xs leading-5 text-[var(--color-text-muted)]"
          >
            {t.realtimeNotice}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={!hasPendingChange || mutation.isPending}
              onClick={() => setReviewOpen(true)}
            >
              {t.reviewChange}
            </Button>
            <div aria-live="polite">
              {saved ? (
                <p className="text-sm font-medium text-[var(--color-success)]">{t.saved}</p>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.confirm.title}</DialogTitle>
            <DialogDescription>{t.confirm.description}</DialogDescription>
          </DialogHeader>

          {currentMode && draftMode ? (
            <div className="space-y-3">
              <div className="grid gap-2 rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                <div>
                  <p className="text-xs font-medium text-[var(--color-text-muted)]">{t.current}</p>
                  <p className="mt-1 text-sm font-semibold text-[var(--color-text)]">
                    {t.modes[currentMode].label}
                  </p>
                </div>
                <span className="hidden text-[var(--color-text-muted)] sm:inline" aria-hidden="true">
                  →
                </span>
                <div>
                  <p className="text-xs font-medium text-[var(--color-text-muted)]">{t.pending}</p>
                  <p className="mt-1 text-sm font-semibold text-[var(--color-text)]">
                    {t.modes[draftMode].label}
                  </p>
                </div>
              </div>
              <p className="text-sm leading-5 text-[var(--color-text)]">
                {t.modes[draftMode].description}
              </p>
            </div>
          ) : null}

          {mutation.isError ? (
            <p role="alert" className="text-sm font-medium text-[var(--color-danger)]">
              {mutation.error.status === 403 ? t.permissionDenied : t.loadError}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setReviewOpen(false)}
            >
              {t.confirm.cancel}
            </Button>
            <Button
              type="button"
              disabled={!draftMode || mutation.isPending}
              onClick={() => {
                if (draftMode) mutation.mutate(draftMode)
              }}
            >
              {mutation.isPending ? t.confirm.saving : t.confirm.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
